import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import LanguageFlag from '@/components/LanguageFlag';

interface LegalPageProps {
  titleSV: string;
  titleEN: string;
  contentSV: string;
  contentEN: string;
}

export default function LegalPage({ titleSV, titleEN, contentSV, contentEN }: LegalPageProps) {
  const [currentLang, setCurrentLang] = useState<'sv' | 'en'>('sv');

  const title = currentLang === 'sv' ? titleSV : titleEN;
  const content = currentLang === 'sv' ? contentSV : contentEN;

  // Update meta tags for SEO
  useEffect(() => {
    document.title = `${title} - Beredskapshubben`;
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', `${title} för Beredskapshubben - Hemberedskapsapp`);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = `${title} för Beredskapshubben - Hemberedskapsapp`;
      document.head.appendChild(meta);
    }

    // Set robots meta for indexing in production
    const robots = document.querySelector('meta[name="robots"]');
    const isProduction = import.meta.env.VITE_ENVIRONMENT === 'prod';
    if (robots) {
      robots.setAttribute('content', isProduction ? 'index, follow' : 'noindex, nofollow');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = isProduction ? 'index, follow' : 'noindex, nofollow';
      document.head.appendChild(meta);
    }
  }, [title]);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Content */}
      <main className="relative py-8 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover opacity-40"
          style={{ backgroundImage: 'url(/background.png)', backgroundPosition: 'center 80%' }}
        />
        <div className="absolute inset-0 bg-black/60 pointer-events-none" />

        <div className="container mx-auto px-4 max-w-4xl relative z-10">
          {/* Language toggle */}
          <div className="flex justify-end mb-4 gap-2">
            <button
              onClick={() => setCurrentLang('sv')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                currentLang === 'sv'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <LanguageFlag lang="sv" />
              Svenska
            </button>
            <button
              onClick={() => setCurrentLang('en')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                currentLang === 'en'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <LanguageFlag lang="en" />
              English
            </button>
          </div>

          <div className="prose prose-sm max-w-none dark:prose-invert bg-card border border-border rounded-lg p-6 md:p-8">
            <ReactMarkdown
              components={{
              h1: ({ children }) => (
                <h1 className="text-3xl font-bold mb-6 text-foreground">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-xl font-medium mt-6 mb-3 text-foreground">{children}</h3>
              ),
              p: ({ children }) => (
                <p className="mb-4 text-muted-foreground leading-relaxed">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="list-disc list-inside mb-4 space-y-2 text-muted-foreground">{children}</ul>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-foreground">{children}</strong>
              ),
              hr: () => <hr className="my-8 border-border" />,
              a: ({ href, children }) => (
                <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
            }}
            >
              {content}
            </ReactMarkdown>
          </div>

          {/* Footer with links to other legal pages */}
          <div className="mt-8 flex flex-wrap gap-4 justify-center text-sm text-muted-foreground">
            <Link to="/terms" className="hover:text-primary transition-colors">
              {currentLang === 'sv' ? 'Användarvillkor' : 'Terms of Use'}
            </Link>
            <span>•</span>
            <Link to="/privacy" className="hover:text-primary transition-colors">
              {currentLang === 'sv' ? 'Integritetspolicy' : 'Privacy Policy'}
            </Link>
            <span>•</span>
            <Link to="/cookies" className="hover:text-primary transition-colors">
              {currentLang === 'sv' ? 'Cookiepolicy' : 'Cookie Policy'}
            </Link>
            <span>•</span>
            <Link to="/community-guidelines" className="hover:text-primary transition-colors">
              {currentLang === 'sv' ? 'Communityregler' : 'Community Guidelines'}
            </Link>
          </div>

          {/* Back to homepage */}
          <div className="mt-8 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{currentLang === 'sv' ? 'Tillbaka till startsidan' : 'Back to homepage'}</span>
            </Link>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
